import React from 'react';

// ─── Card Variants ──────────────────────────────────────────

export interface CardProps {
  /** Card visual depth */
  variant?: 'bento' | 'item' | 'feature' | 'hero';
  /** Enable hover effects */
  hoverable?: boolean;
  /** Header content (rendered above body with border) */
  header?: React.ReactNode;
  /** Footer content (rendered below body with border) */
  footer?: React.ReactNode;
  /** Click handler (makes card clickable) */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Card content */
  children: React.ReactNode;
}

/**
 * Canonical Card component – replaces all 12+ inline card patterns.
 *
 * Uses the 3-layer depth system from the Dark Canvas design:
 * - `bento`: Layer 2 card (bg-card → for section containers, main content areas)
 * - `item`: Layer 3 card (bg-surface → for items INSIDE bento cards)
 * - `feature`: Layer 2 with xl radius (for feature/module cards)
 * - `hero`: Layer 2 with xl radius, internal purple glow (for page hero sections)
 *
 * All cards use token-based colors, borders, and radii.
 */
export const Card: React.FC<CardProps> & {
  Header: typeof CardHeader;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
} = Object.assign(
  ({
    variant = 'bento',
    hoverable = false,
    header,
    footer,
    children,
    onClick,
    className = '',
  }: CardProps) => {
    const variantStyles: Record<string, string> = {
      bento: [
        'bg-bg-card border border-default rounded-card',
        'shadow-card',
      ].join(' '),

      item: [
        'bg-bg-surface border-subtle border rounded-md',
      ].join(' '),

      feature: [
        'bg-bg-card border border-default rounded-card-lg',
        'shadow-card',
      ].join(' '),

      hero: [
        'bg-bg-card border border-default rounded-card-lg',
        'shadow-card relative overflow-hidden',
      ].join(' '),
    };

    const hoverStyles: Record<string, string> = {
      bento: 'hover:border-strong hover:bg-bg-card-hover',
      item: 'hover:border-default hover:bg-bg-surface-hover',
      feature: 'hover:border-strong hover:-translate-y-[3px]',
      hero: '',
    };

    const cardClasses = [
      variantStyles[variant] || variantStyles.bento,
      'transition-all duration-300 ease-spring-smooth',
      hoverable || onClick ? hoverStyles[variant] || '' : '',
      onClick ? 'cursor-pointer' : '',
      className,
    ].filter(Boolean).join(' ');

    return (
      <div className={cardClasses} onClick={onClick}>
        {/* Hero internal purple glow */}
        {variant === 'hero' && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: '-80px',
              left: '30%',
              width: '400px',
              height: '250px',
              background: 'radial-gradient(ellipse, rgba(120,80,200,0.10) 0%, transparent 70%)',
            }}
          />
        )}

        {header && (
          <div className="px-4 sm:px-6 py-4 border-b border-subtle">
            {header}
          </div>
        )}

        {!header && !footer ? children : (
          <div className="p-4 sm:p-6 relative z-[1]">
            {children}
          </div>
        )}

        {footer && (
          <div className="px-4 sm:px-6 py-3 border-t border-subtle">
            {footer}
          </div>
        )}
      </div>
    );
  },
  {
    Header: CardHeader,
    Content: CardContent,
    Footer: CardFooter,
  }
);

// ─── Sub-components ─────────────────────────────────────────

function CardHeader({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`px-4 sm:px-6 py-4 border-b border-subtle ${className}`}>
      {children}
    </div>
  );
}

function CardContent({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`p-4 sm:p-6 ${className}`}>
      {children}
    </div>
  );
}

function CardFooter({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`px-4 sm:px-6 py-3 border-t border-subtle ${className}`}>
      {children}
    </div>
  );
}

export default Card;
